/**
 * Infinite Scroll for Blog Articles
 * Automatically loads more articles when user scrolls near bottom
 */
class BlogInfiniteScroll {
	constructor() {
		this.isMobile = window.innerWidth < 750;
		this.isLoading = false;
		this.hasMorePages = false;
		this.nextPageUrl = null;
		this.articleGrid = null;
		this.paginationWrapper = null;
		this.loadingIndicator = null;
		this.scrollListenerAttached = false;

		this.init();
	}

	init() {
		// Try to find the article grid
		let gridElement = document.querySelector('#blog-article-grid');
		if (gridElement && gridElement.tagName === 'UL') {
			this.articleGrid = gridElement;
		} else if (gridElement) {
			// It's a div, find the UL inside
			this.articleGrid = gridElement.querySelector('ul.blog-grid');
		}

		this.paginationWrapper = document.querySelector('.pagination-wrapper');

		if (!this.articleGrid) {
			console.warn('[BlogInfiniteScroll] Article grid UL not found');
			return;
		}

		this.setupPagination();
		this.createLoadingIndicator();

		if (!this.scrollListenerAttached) {
			this.attachScrollListener();
			this.scrollListenerAttached = true;
		}

		// Re-check on resize
		window.addEventListener('resize', () => {
			const wasMobile = this.isMobile;
			this.isMobile = window.innerWidth < 750;

			// If switching from desktop to mobile, reinitialize
			if (!wasMobile && this.isMobile) {
				this.setupPagination();
				if (!this.scrollListenerAttached) {
					this.attachScrollListener();
					this.scrollListenerAttached = true;
				}
			}
		});
	}

	setupPagination() {
		if (!this.paginationWrapper) {
			this.hasMorePages = false;
			return;
		}

		const nextButton = this.paginationWrapper.querySelector('.pagination__item--prev');

		if (nextButton && nextButton.href) {
			this.hasMorePages = true;
			this.nextPageUrl = nextButton.href;

			// Hide pagination
			this.paginationWrapper.style.display = 'none';
		} else {
			this.hasMorePages = false;
		}
	}

	createLoadingIndicator() {
		this.loadingIndicator = document.createElement('div');
		this.loadingIndicator.className = 'infinite-scroll-loading';
		this.loadingIndicator.innerHTML = `
      <div class="loading-spinner">
        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>
    `;
		this.loadingIndicator.style.display = 'none';

		const articleGridContainer = document.querySelector('#BlogArticleGridContainer, .blog-article-grid-container');
		if (articleGridContainer) {
			articleGridContainer.appendChild(this.loadingIndicator);
		}
	}

	attachScrollListener() {
		const scrollHandler = () => {
			if (this.isLoading || !this.hasMorePages || !this.articleGrid) {
				return;
			}

			// Get all article items
			const allArticles = Array.from(this.articleGrid.querySelectorAll('li.article-item'));
			if (allArticles.length === 0) {
				return;
			}

			// Find articles in the second-to-last row
			// Group articles by their top position to identify rows
			const articlesByRow = new Map();
			allArticles.forEach(article => {
				const rect = article.getBoundingClientRect();
				const topPosition = Math.round(rect.top);

				if (!articlesByRow.has(topPosition)) {
					articlesByRow.set(topPosition, []);
				}
				articlesByRow.get(topPosition).push(article);
			});

			// Get all unique row positions sorted
			const rowPositions = Array.from(articlesByRow.keys()).sort((a, b) => a - b);

			if (rowPositions.length < 2) {
				// If there's only one row, use the last article
				const lastArticle = allArticles[allArticles.length - 1];
				const lastArticleRect = lastArticle.getBoundingClientRect();
				const viewportHeight = window.innerHeight;
				const isLastArticleVisible = lastArticleRect.top < viewportHeight + 100;

				if (isLastArticleVisible) {
					this.loadMoreArticles();
				}
				return;
			}

			// Get the second-to-last row position
			const secondToLastRowPosition = rowPositions[rowPositions.length - 2];
			const secondToLastRowArticles = articlesByRow.get(secondToLastRowPosition);

			if (!secondToLastRowArticles || secondToLastRowArticles.length === 0) {
				return;
			}

			// Check if any article in the second-to-last row is visible
			const viewportHeight = window.innerHeight;
			const isSecondToLastRowVisible = secondToLastRowArticles.some(article => {
				const rect = article.getBoundingClientRect();
				return rect.top < viewportHeight + 100;
			});

			if (isSecondToLastRowVisible) {
				this.loadMoreArticles();
			}
		};

		// Throttle scroll event
		let ticking = false;
		this.scrollListener = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					scrollHandler();
					ticking = false;
				});
				ticking = true;
			}
		};

		window.addEventListener('scroll', this.scrollListener);
	}

	async loadMoreArticles() {
		if (this.isLoading || !this.hasMorePages || !this.nextPageUrl) {
			return;
		}

		this.isLoading = true;
		this.showLoadingIndicator();

		try {
			const response = await fetch(this.nextPageUrl);
			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			// Extract new articles
			let newArticlesGrid = doc.querySelector('#blog-article-grid');
			let newArticles = [];

			if (newArticlesGrid && newArticlesGrid.tagName === 'UL') {
				newArticles = newArticlesGrid.querySelectorAll(':scope > li.article-item');
			} else if (newArticlesGrid) {
				const ul = newArticlesGrid.querySelector('ul.blog-grid');
				if (ul) {
					newArticles = ul.querySelectorAll(':scope > li.article-item');
				}
			}

			if (newArticles.length > 0) {
				// Get current articles count
				const currentArticlesCount = this.articleGrid.querySelectorAll(':scope > li.article-item').length;

				// Calculate the current global offset to maintain the grid pattern
				const currentPage = this.paginationWrapper ? parseInt(this.paginationWrapper.dataset.page || '1') : 1;
				const articlesPerPage = 7;
				const currentOffset = (currentPage - 1) * articlesPerPage;

				// Append new articles to existing grid
				newArticles.forEach((article, index) => {
					// Calculate global index for this article
					const globalIndex = currentOffset + currentArticlesCount + index + 1;
					const remainder = globalIndex % 7;

					// Determine if this article should be large
					const shouldBeLarge = (remainder === 1 || remainder === 0);

					// Clone the article
					const clonedArticle = article.cloneNode(true);

					// Update the class to match the grid pattern
					if (shouldBeLarge && !clonedArticle.classList.contains('article-large')) {
						clonedArticle.classList.add('article-large');
					} else if (!shouldBeLarge && clonedArticle.classList.contains('article-large')) {
						clonedArticle.classList.remove('article-large');
					}

					this.articleGrid.appendChild(clonedArticle);
				});

				// Update pagination info
				const newPaginationWrapper = doc.querySelector('.pagination-wrapper');
				if (newPaginationWrapper) {
					const newNextButton = newPaginationWrapper.querySelector('.pagination__item--prev');

					if (newNextButton && newNextButton.href) {
						this.nextPageUrl = newNextButton.href;
						this.hasMorePages = true;
					} else {
						this.hasMorePages = false;
						this.nextPageUrl = null;
					}

					// Update the hidden pagination wrapper
					if (this.paginationWrapper) {
						this.paginationWrapper.dataset.page = newPaginationWrapper.dataset.page;
					}
				} else {
					this.hasMorePages = false;
				}

				// Trigger animation if enabled
				if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
					const newItems = Array.from(newArticles);
					newItems.forEach((item, index) => {
						setTimeout(() => {
							const addedItem = this.articleGrid.children[this.articleGrid.children.length - newArticles.length + index];
							if (addedItem) {
								addedItem.classList.add('animate--slide-in');
							}
						}, index * 50);
					});
				}

				// Dispatch event for other scripts (like sorting) to react to new articles
				window.dispatchEvent(new CustomEvent('blogArticlesLoaded'));

			} else {
				this.hasMorePages = false;
				console.warn('[BlogInfiniteScroll] No articles found in response');
			}
		} catch (error) {
			console.error('[BlogInfiniteScroll] Error loading more articles:', error);
			this.hasMorePages = false;
		} finally {
			this.isLoading = false;
			this.hideLoadingIndicator();
		}
	}

	showLoadingIndicator() {
		if (this.loadingIndicator) {
			this.loadingIndicator.style.display = 'flex';
		}
	}

	hideLoadingIndicator() {
		if (this.loadingIndicator) {
			this.loadingIndicator.style.display = 'none';
		}
	}

	// Method to reinitialize after filter changes
	reinitialize() {
		let gridElement = document.querySelector('#blog-article-grid');
		if (gridElement && gridElement.tagName === 'UL') {
			this.articleGrid = gridElement;
		} else if (gridElement) {
			this.articleGrid = gridElement.querySelector('ul.blog-grid');
		}

		this.paginationWrapper = document.querySelector('.pagination-wrapper');

		if (this.paginationWrapper) {
			this.setupPagination();
		} else {
			this.hasMorePages = false;
		}

		// Reset loading state
		this.isLoading = false;
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => {
		window.blogInfiniteScroll = new BlogInfiniteScroll();
	});
} else {
	window.blogInfiniteScroll = new BlogInfiniteScroll();
}
